import React from 'react';
import useStyles from './use_styles';
import { LabelInterface } from './types';
import { useTheme } from '@veneer/theme';
import highContrastMediaQuery from '../../../utils/high_contrast_media_query';
import useIsOverflow from '../../../utils/use_is_overflow';
import mergeRefs from 'react-merge-refs';
import Tooltip from '../../../tooltip';
import useSafeTimeout from '../../../utils/use_safe_timeout';
import useInputStyles from '../use_input_styles';
import isElementOverflowing from '../../../utils/is_element_overflowing';
import labelI18n from './i18n';
import { i18nCounterString } from './utils';

const Label = React.forwardRef<HTMLLabelElement, LabelInterface>(
  (
    {
      applyTo = 'input',
      children,
      disabled = false,
      disableLabelShrinking = false,
      handleLabelMouseDown,
      id,
      inputRef,
      inside = false,
      openOrFilled,
      required = false,
      truncate = false,
      wrap = false,
      counter = false,
      counterLimit,
      countCallback,
      leadingWidth,
      trailingWidth,
      i18n: i18nProp,
      ...other
    },
    ref
  ) => {
    const theme = useTheme();
    const pill = theme.shape === 'pill';
    const A11Y_ANNOUNCER_DELAY = 1000;
    const A11Y_ANNOUNCER_ON_FOCUS = 100;
    const containerStyle = useInputStyles({
      leadingWidth,
      trailingWidth,
    });
    const [counterSize, setCounterSize] = React.useState(
      counterLimit !== undefined ? counterLimit : 0
    );

    const style = useStyles({ element: applyTo });
    const [isOverflow, setIsOverflow] = React.useState(false);
    const labelRef = React.useRef<HTMLLabelElement>(null);
    const trailingLabelRef = React.useRef<HTMLSpanElement>(null);
    const handleRef = mergeRefs([ref, labelRef]);
    const labelIsOverflow = useIsOverflow({ ref: labelRef, checkResize: true });
    const i18n = React.useMemo(
      () => ({
        ...labelI18n,
        ...i18nProp,
      }),
      [i18nProp]
    );

    const handleCounts = React.useCallback(() => {
      if (countCallback !== undefined)
        return countCallback(inputRef!.current!.value.toString());
      if (counterLimit !== undefined)
        return counterLimit - inputRef!.current!.value.length;
      return inputRef!.current!.value.length;
    }, [countCallback, inputRef, counterLimit]);

    const handleCounterTextA11y = React.useCallback(
      (currentCounterValue?: number) => {
        return i18nCounterString(
          counterLimit !== undefined
            ? i18n.countRemainingA11y
            : i18n.countUsedA11y,
          currentCounterValue !== undefined ? currentCounterValue : counterSize,
          counterLimit
        );
      },
      [counterLimit, i18n, counterSize]
    );

    const [counterTextA11y, setCounterTextA11y] = React.useState(
      handleCounterTextA11y()
    );

    const [setReadyTimeout] = useSafeTimeout();
    const [hasScroll, setHasScroll] = React.useState(false);
    const trailingLabel = (
      <span
        ref={trailingLabelRef}
        css={(inside || truncate) && style.label.truncate}
      >
        {children}
      </span>
    );

    const withTooltip = (
      <Tooltip
        portal
        content={children}
        forTruncation
        placement="top-start"
        useJsPositioning
        userSelect="text"
        childRef={trailingLabelRef}
      >
        {trailingLabel}
      </Tooltip>
    );

    const withCounter = (
      <>
        <div aria-hidden="true">
          <span>
            &nbsp;
            {i18nCounterString(
              counterLimit !== undefined ? i18n.countRemaining : i18n.countUsed,
              counterSize,
              counterLimit
            )}
          </span>
        </div>
        <div role="status" css={style.counter.a11y}>
          <span>{counterTextA11y}</span>
        </div>
      </>
    );

    React.useEffect(() => {
      const input = inputRef?.current;
      if (!input) return;
      const updateLength = () => {
        const currentCounterValue = handleCounts();
        setCounterSize(currentCounterValue);
        setReadyTimeout(() => {
          setCounterTextA11y(handleCounterTextA11y(currentCounterValue));
        }, A11Y_ANNOUNCER_DELAY);
      };

      const announceOnFocus = () => {
        setCounterTextA11y('');
        setReadyTimeout(() => {
          setCounterTextA11y(handleCounterTextA11y());
        }, A11Y_ANNOUNCER_ON_FOCUS);
      };

      setHasScroll(isElementOverflowing(input, true));

      const resizeObserver = new ResizeObserver(() => {
        setHasScroll(isElementOverflowing(input, true));
        setIsOverflow(
          labelIsOverflow || isElementOverflowing(trailingLabelRef?.current)
        );
      });

      input.addEventListener('input', updateLength);
      input.addEventListener('focus', announceOnFocus);
      resizeObserver.observe(input);

      return () => {
        input.removeEventListener('input', updateLength);
        input.removeEventListener('focus', announceOnFocus);
        resizeObserver.disconnect();
      };
    }, [
      counterLimit,
      setReadyTimeout,
      inputRef,
      handleCounterTextA11y,
      labelIsOverflow,
      handleCounts,
      counterSize,
    ]);

    // needed to be able to click on the inside label and focus input
    const handleLabelClick = React.useCallback(() => {
      if (disabled || !inside) return;
      inputRef?.current?.focus();
      handleLabelMouseDown?.();
    }, [disabled, handleLabelMouseDown, inputRef, inside]);

    return (
      // false positive, this is a mouse only, screen readers will read the full label and ignore the tooltip existence.
      // eslint-disable-next-line jsx-a11y/click-events-have-key-events
      <label
        ref={handleRef}
        id={id}
        css={[
          style.label.base,
          counter && style.label.counter,
          required && !disabled && style.label.required,
          disabled && style.label.disabled,
          truncate && style.label.truncate,
          inside && [
            style.insideLabel.base,
            disableLabelShrinking && style.insideLabel.shrink,
            openOrFilled && style.insideLabel.shrink,
            containerStyle.input.label.base,
            pill && containerStyle.input.label.base,
            hasScroll && style.counter.scroll,
          ],
          !inside && [
            style.separateLabel.base,
            wrap && style.separateLabel.wrap,
          ],
          highContrastMediaQuery(theme),
        ]}
        onClick={handleLabelClick}
        {...other}
      >
        {isOverflow ? withTooltip : trailingLabel}
        {counter && withCounter}
      </label>
    );
  }
);

Label.displayName = 'Label';

export default Label;
